import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useLoadChatImagesOnOpen, useOpenUserCardTab, useScrollRef } from './hooks';
import { clearActiveReplyMessage } from '../../actions/chatWindow/actions';
import { ChatWindowLayout } from '../../components/ChatWindowLayout';
import { CHATBOT_ID } from '../../constants/chatbotId';

import { chatListSelector, currentOpenChatSelector, hostTypeSelector } from '../../selectors';
import { countClosedChatsSelector } from '../../selectors/countClosedChatsSelector';
import { ERequestApiStatus } from '../../types/appState';
import { TAnalyticsKey } from '../../types/chatHint';
import { EEventQueueEvents } from '../../types/eventQueue';
import { EHostType } from '../../types/hostType';
import { useApplicationContext } from '../../utils/applicationContext';
import { isInIframe } from '../../utils/isInIframe';
import { ChatHeaderChatbotContainer } from '../ChatHeaderChatbotContainer';
import { ChatHeaderPinFilterContainer } from '../ChatHeaderPinFilterContainer';
import { ChatHeaderUserContainer } from '../ChatHeaderUserContainer';
import { ChatScrollContainer } from '../ChatScrollContainer';
import { ChatWindowErrorContainer } from '../ChatWindowErrorContainer';
import { ChatWindowInitialContainer } from '../ChatWindowInitialContainer';
import { FullscreenGallery } from '../FullscreenGallery';
import { MessageInputFieldContainer } from '../MessageInputFieldContainer';
import { OfferMessageTooltipProvider } from '../ChatMessageContainer/containers/OfferMessageContainer/provider';
import { ScammerAlertContainer } from '../ScammerAlertContainer';
import { UnlockChatButtonContainer } from '../UnlockChatButtonContainer';
import { useChatsWithTodayMessage } from './hooks/useChatsWithTodayMessage';
import { sendUXSEvent } from '../../utils/uxs';
import { ChatHeaderCardPreviewContainer } from '../ChatHeaderCardPreviewContainer';
import { selectIsNewChatFromLinkOpened } from '../../selectors/newChatFromLinkOpenedSelector';
import { ReplyScrollProvider } from '../ReplyScrollProvider';
import { useRuntimeSetting } from '../../hooks/useRuntimeSetting';
import { useUpdateChatIdInUrl } from '../../hooks/useUpdateChatIdInUrl';

export const ChatWindowContainer: React.FC = () => {
  const dispatch = useDispatch();
  const currentChat = useSelector(currentOpenChatSelector);
  const hostType = useSelector(hostTypeSelector);
  const countClosedChats = useSelector(countClosedChatsSelector);
  const chatList = useSelector(chatListSelector);
  const isNewChatFromLinkOpened = useSelector(selectIsNewChatFromLinkOpened);

  const { custom, eventQueue } = useApplicationContext();
  const { messagesMap } = custom;

  const status = currentChat ? currentChat.status : ERequestApiStatus.Initial;
  const isChatbot = currentChat?.chatId === CHATBOT_ID;
  const backgroundImageUrl = useRuntimeSetting<string>('frontend-chats.backgroundImage', '');
  const isSendUXFBMonitoringEnabled = useRuntimeSetting<boolean>('frontend-chats.sendUXFBMonitoring.Enabled', false);
  const { scrollRef, scrollToBottom, onScrollHeightChange } = useScrollRef();
  const rootElementRef = React.useRef<HTMLDivElement | null>(null);

  const [analyticsKey, setHintAnalyticsKey] = React.useState<TAnalyticsKey | null>(null);

  useOpenUserCardTab();
  useUpdateChatIdInUrl();
  useLoadChatImagesOnOpen();

  const [isEventUXFBSended, setEventUXFBSended] = React.useState(false);

  const isNeededCallUXFB = useChatsWithTodayMessage(
    chatList.chats,
    messagesMap,
    countClosedChats,
    isEventUXFBSended,
    isSendUXFBMonitoringEnabled,
  );

  const handleWindowClose = React.useCallback(() => {
    if (window.parent) {
      window.parent.postMessage(
        {
          type: EEventQueueEvents.OpenNewChat,
          payload: { chatId: '' },
        },
        '*',
      );

      return;
    }
  }, []);

  React.useEffect(() => {
    const isIframe = isInIframe();
    if (isIframe) {
      window.addEventListener('beforeunload', handleWindowClose, { capture: true, passive: true });
      window.addEventListener('pagehide', handleWindowClose, { capture: true, passive: true });
    }
  }, []);

  React.useEffect(() => {
    const isIframe = isInIframe();
    try {
      if (isIframe && window.parent) {
        window.parent.postMessage(
          {
            type: EEventQueueEvents.OpenNewChat,
            payload: { chatId: currentChat?.chatId || '' },
          },
          '*',
        );

        return;
      }

      eventQueue.emit(EEventQueueEvents.OpenNewChat, { chatId: currentChat?.chatId || '' });
    } catch {
      // do nothing
    }
  }, [currentChat?.chatId, eventQueue]);

  React.useEffect(() => {
    if (countClosedChats && isNeededCallUXFB && !isEventUXFBSended) {
      sendUXSEvent('chatmonitoring');
      setEventUXFBSended(true);
    }
  }, [countClosedChats, isEventUXFBSended, isNeededCallUXFB]);

  React.useEffect(() => {
    const currentChatId = currentChat?.chatId;

    if (currentChatId) {
      dispatch(clearActiveReplyMessage());
    }
  }, [currentChat?.chatId, dispatch]);

  const canShowNewChat = hostType === EHostType.Frame || isNewChatFromLinkOpened;

  if (status === ERequestApiStatus.Initial && !canShowNewChat) {
    return <ChatWindowInitialContainer />;
  }

  return (
    <OfferMessageTooltipProvider rootRef={rootElementRef}>
      <ReplyScrollProvider>
        <ChatWindowLayout
          backgroundImageUrl={backgroundImageUrl}
          headerNode={
            <>
              <FullscreenGallery />
              <ScammerAlertContainer />
              {isChatbot ? (
                <>
                  <ChatHeaderChatbotContainer />
                  <ChatHeaderPinFilterContainer />
                </>
              ) : (
                <>
                  <ChatHeaderUserContainer />
                  <ChatHeaderCardPreviewContainer />
                </>
              )}
              <ChatWindowErrorContainer />
            </>
          }
          bodyNode={
            <ChatScrollContainer
              scrollRef={scrollRef}
              scrollToBottom={scrollToBottom}
              rootRef={rootElementRef}
              onHintQuestionChange={setHintAnalyticsKey}
            />
          }
          footerNode={
            <>
              <UnlockChatButtonContainer />
              <MessageInputFieldContainer onHeightChange={onScrollHeightChange} analyticsKey={analyticsKey} />
            </>
          }
        />
      </ReplyScrollProvider>
    </OfferMessageTooltipProvider>
  );
};
